import { Injectable } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { FiLogErrorService } from '@fi-shared/fi-shared-services/fi-log-error.service';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import { FiBackendCacheService } from '@fi-shared/fi-backend/fi-backend-cache.service';
import { FiApplicationLanguageType } from '@fi-shared/fi-content-language-shared/fi-application-language-type';

export interface IApplicationLanguageLabels {
    [prop: string]: string;
}

let texts: IApplicationLanguageLabels = {};

@Injectable()
export class AppTextService {
    private _onInitialize$ = new ReplaySubject<FiApplicationLanguageType>(1);
    get onInitialize$() {
        return this._onInitialize$.asObservable();
    }

    private _isAppLanguageLoading$ = new ReplaySubject<boolean>(1);
    get isAppLanguageLoading$() {
        return this._isAppLanguageLoading$.asObservable();
    }

    private _currentApplicationLanguage: FiApplicationLanguageType = FiApplicationLanguageType.English;
    getCurrentApplicationLanguage(): FiApplicationLanguageType {
        return this._currentApplicationLanguage;
    }

    constructor(
        private logErrorService: FiLogErrorService,
        private fiBackend: FiBackendService,
        private backendCacheService: FiBackendCacheService,
    ) {
        this._onInitialize$.subscribe((v) => (this._currentApplicationLanguage = v));
    }

    private _textsLoaded$ = new ReplaySubject<void>(1);
    get textsLoaded$() {
        return this._textsLoaded$.asObservable();
    }

    public loadApplicationLanguageLabels(languageId: FiApplicationLanguageType, skipAppLanguageLoading?: boolean) {
        if (!skipAppLanguageLoading) {
            this._isAppLanguageLoading$.next(true);
        }

        const action = 'Localization/ApplicationLanguageLabel/GetApplicationLanguageLabelsDictionary/';
        const opts = {
            supressErrorMessage: true,
        };
        const url = this.fiBackend.getRequestUrl(action, opts);
        // https://chainformation.atlassian.net/browse/FI-4097
        // clear old application languages from localStorage
        this.backendCacheService.deleteCacheItems((key) => {
            const result = key.indexOf(url) !== 0 && key.indexOf(action) !== -1;
            return result;
        });
        const promise = this.fiBackend.fetchData<IApplicationLanguageLabels>(action + languageId, opts);

        promise
            .then((response) => {
                const texts = response;
                this.setTexts(texts);
                this._onInitialize$.next(languageId);
            })
            .finally(() => {
                if (!skipAppLanguageLoading) {
                    this._isAppLanguageLoading$.next(false);
                }
            });

        return promise;
    }

    private setTexts(_texts: IApplicationLanguageLabels) {
        texts = _texts;
        this._textsLoaded$.next();
    }
    public addTexts(_texts: IApplicationLanguageLabels) {
        Object.assign(texts, _texts);
        this._textsLoaded$.next();
    }

    private isNumeric(str) {
        return (
            !isNaN(str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
            !isNaN(parseFloat(str))
        ); // ...and ensure strings of whitespace fail
    }

    private getAppText(id: string) {
        if (id && this.isNumeric(id.toString())) {
            this.logErrorService.logError('getAppText numeric key ' + id);
        }

        if (texts.hasOwnProperty(id)) return texts[id];

        if (id) {
            //this.logErrorService.logError('getAppText no translation ' + id);
        }

        if (id) return id.toString();

        return id;
    }

    get(textId: string, format?: string | any): string {
        let result = this.getAppText(textId);

        if (format && result) {
            Object.keys(format).forEach(function (pattern) {
                const replace = format[pattern];
                result = result.split(pattern).join(replace);
            });
        }
        return result;
    }
}
